import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { css } from "@emotion/react";
import Transition from "react-transition-group/Transition";
import { Icon } from "../icon/Icon";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";

export interface IProps {
  children: React.ReactNode;
  displayed: boolean;
  closeHandler(any): void;
  heightStyle?: string;
  widthStyle?: string;
  maxWidthStyle?: string;
  maxHeightStyle?: string;
  styles?: any;
}

const enterDuration = 400;
const exitDuration = 400;

export const ModalDialogWithClose: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const {
    children,
    displayed,
    closeHandler,
    heightStyle,
    widthStyle,
    maxWidthStyle,
    maxHeightStyle,
    styles = {},
  } = props;
  const {
    exOverlayStyle,
    exInnerStyle,
    exInnerContentStyle,
    exCloseButtonStyle,
    exCloseStyle,
  } = styles;

  const [isMounted, setIsMounted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const closeModal = (e) => {
    if (displayed) {
      closeHandler(e);
    }
  };

  const onClickOverlay = (e: any): void => {
    if (
      ref.current &&
      (ref.current === e.target || !ref.current.contains(e.target))
    ) {
      closeModal(e);
    }
  };

  let innerStyles = innerStyle;
  if (heightStyle) {
    innerStyles = css`
      ${innerStyles};
      height: ${heightStyle};
    `;
  }
  if (widthStyle) {
    innerStyles = css`
      ${innerStyles};
      width: ${widthStyle};
    `;
  }
  if (maxWidthStyle) {
    innerStyles = css`
      ${innerStyles};
      max-width: ${maxWidthStyle};
    `;
  }
  if (maxHeightStyle) {
    innerStyles = css`
      ${innerStyles};
      max-height: ${maxHeightStyle};
    `;
  }

  return (
    isMounted &&
    ReactDOM.createPortal(
      <Transition
        timeout={{
          enter: enterDuration,
          exit: exitDuration,
        }}
        in={displayed}
        unmountOnExit={true}
        nodeRef={ref}
      >
        {(state: any): JSX.Element => (
          <div
            style={{
              ...defaultBlockStyle,
              ...transitionBlockStyles[state],
            }}
            css={[blockStyle, exOverlayStyle]}
            onClick={onClickOverlay}
            ref={ref}
          >
            <div
              style={{
                ...defaultInnerStyle,
                ...transitionInnerStyles[state],
              }}
              css={[innerStyles, exInnerStyle]}
              className="popupInner"
            >
              <div css={[innerContentStyle, exInnerContentStyle]}>
                {children}
              </div>
              <div
                onClick={closeModal}
                css={[closeButtonStyle, exCloseButtonStyle]}
              >
                <Icon
                  styleKey="close"
                  styles={[closeButtonIconStyle, exCloseStyle]}
                />
              </div>
            </div>
          </div>
        )}
      </Transition>,
      document.getElementById("modal")
    )
  );
};

const blockStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(10, 10, 10, 0.2);
  width: 100%;
  height: 100%;
  z-index: 1000;
`;
const innerStyle = css`
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  width: 90%;
  height: 100%;
  max-height: 80%;
  max-width: 540px;
  background: #fff;
  border-radius: ${borderRadius.normal};
  box-shadow: 0 6px 30px rgba(10, 10, 10, 0.1);
  box-sizing: border-box;
  will-change: transform;
`;
const innerContentStyle = css`
  //height: calc(100% - 3rem);
  width: 100%;
  margin-top: 3rem;
  box-sizing: border-box;
`;
const closeButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  text-align: center;
  box-sizing: border-box;
  font-size: 24px;
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.3s ease;
  backface-visibility: hidden;
  z-index: 100;
  &:hover {
    opacity: 1;
  }
`;
const closeButtonIconStyle = css`
  line-height: 3rem;
`;

export const defaultBlockStyle: any = {
  transition: `all ${enterDuration}ms ease`,
  opacity: 0,
};

export const transitionBlockStyles: any = {
  entering: {
    opacity: 0,
  },
  entered: {
    opacity: 1,
  },
  exiting: {
    transition: `all ${exitDuration}ms ease`,
  },
  exited: {
    opacity: 0,
    display: "none",
  },
};

export const defaultInnerStyle: any = {
  transition: `all ${enterDuration}ms ease`,
  transform: "translate(-50%, -50%) scale(0.9)",
};

export const transitionInnerStyles: any = {
  entering: {
    transform: "translate3d(-50%, -50%, 0) scale(0.9)",
  },
  entered: {
    transform: "translate3d(-50%, -50%, 0) scale(1)",
  },
  exiting: {
    transition: `all ${exitDuration}ms ease`,
  },
  exited: {
    transform: "translate3d(-50%, -50%, 0) scale(0.9)",
    display: "none",
  },
};
